import { Component, Input } from '@angular/core';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { Layout } from 'modules/layout';
import { Pipes } from 'modules/pipes';
import { ProductService } from 'services/product.service';
import { SalesService } from 'services/sales.service';

@Component({
	selector: 'order-commission-details',
	templateUrl: './order-commission-details.component.html',
	imports: [Layout, Pipes]
})
export class OrderCommissionDetailsComponent {

	private _order?: OrderModel;
	@Input() set order(value: OrderModel | undefined) {
		this._order = value;
		this.calculateCommissionValues();
	}
	get order() { return this._order; }

	constructor(private readonly productService: ProductService, private readonly salesService: SalesService) { }

	public warningAcvExtendNegativeThereforeZero = false;
	public warningAcvIncreaseNegativeThereforeZero = false;
	public maxNewPeriodInYears = 0;
	public discontinuedPlansAnnualValueForRenewal = 0;
	public newPlansAnnualValueForRenewal = 0;
	public sumOfProductsWithNoPeriod = 0;
	public sumOfNonCommissionableProducts = 0;
	public sumOfRenewableCommissionableProducts = 0;
	public math = Math;
	public acvExtend = 0;
	public acvIncrease = 0;
	public upfrontPaidOrder = false;
	public totalDiscount: number = 0;

	private calculateCommissionValues() {
		if (!this.order) return;

		// TODO: PBI 23155, Når ordre rabat skal ændres til frontend redigering, skal nedenstående udregning kunne håndtere rabat som ikke benyttes som modregning, derfor skal rabatten fratrækkes total prisen inden de øvrige udregninger foretages.

		this.totalDiscount = this.salesService.getOrderLinesDiscountSum(this.order.orderLines) * -1;

		const productVariants = this.order.orderLines.flatMap(line => line.productVariant!);
		const periods = productVariants.map(pv => this.productService.getVariantPeriod(pv));
		this.maxNewPeriodInYears = Math.max(...periods.map(p => p ?? 0)) / 12;
		this.discontinuedPlansAnnualValueForRenewal = this.order.orderProductPlanDiscontinuations.reduce((sum, discontinuedPlan) => sum + (discontinuedPlan.productPlan?.annualValueForRenewal ?? 0), 0) + (this.order.externalAnnualValueForRenewal ?? 0);
		this.newPlansAnnualValueForRenewal = this.order.orderLines.reduce((sum, orderLine) => sum + (this.productService.getVariantPeriod(orderLine.productVariant!) != null ? (orderLine.totalPrice / ((this.productService.getVariantPeriod(orderLine.productVariant!) ?? 0) / 12)) : 0), 0);
		this.sumOfProductsWithNoPeriod = this.order.orderLines.reduce((sum, orderLine) => sum + (this.productService.getVariantPeriod(orderLine.productVariant!) == null ? orderLine.totalPrice : 0), 0);
		this.sumOfNonCommissionableProducts = this.order.orderLines.reduce((sum, orderLine) => sum + (orderLine.productVariant!.product?.commissionDisabled ? orderLine.totalPrice : 0), 0);

		this.acvExtend = this.maxNewPeriodInYears * this.discontinuedPlansAnnualValueForRenewal + (this.getTotalAdjustmentAmount() + (this.hasExternalAdjustments() ? this.totalDiscount : 0)) + this.calculateSupersededRemainingAmountForOrder();

		this.sumOfRenewableCommissionableProducts = this.getTotalPrice() - this.sumOfProductsWithNoPeriod - this.sumOfNonCommissionableProducts;

		this.acvIncrease = this.sumOfRenewableCommissionableProducts + (this.getTotalAdjustmentAmount() + (this.hasExternalAdjustments() ? this.totalDiscount : 0)) + this.calculateSupersededRemainingAmountForOrder() - (this.acvExtend < 0 ? 0 : this.acvExtend);

		this.upfrontPaidOrder = this.order.orderLines.some(ol => (ol.numberOfInstallments || ol.paymentInterval == 0) && this.productService.getVariantPeriod(ol.productVariant!) != null);

		const acvIncreaseComparisonValue = this.upfrontPaidOrder ? 0 : this.acvIncrease < 0 ? 0 : this.acvIncrease;
		if (Math.abs(Math.floor(acvIncreaseComparisonValue) - Math.floor(this.salesService.getAcvIncreaseCommission(this.order.orderLines))) > 1) {
			console.error(`ACV Increase (${acvIncreaseComparisonValue}) and ACV Increase Commission (${this.salesService.getAcvIncreaseCommission(this.order.orderLines)}) differ by more than 1. This may indicate a calculation error.`);
		}

		const acvExtendComparisonValue = this.upfrontPaidOrder ? 0 : (this.acvExtend < 0 ? 0 : this.acvExtend) + (this.acvIncrease < 0 ? this.acvIncrease : 0);
		if (Math.abs(Math.floor(acvExtendComparisonValue) - Math.floor(this.salesService.getAcvExtendCommission(this.order.orderLines))) > 1) {
			console.error(`ACV Extend (${acvExtendComparisonValue}) and ACV Extend Commission (${this.salesService.getAcvExtendCommission(this.order.orderLines)}) differ by more than 1. This may indicate a calculation error.`);
		}

		// Make check if totaladjustment and remaining value is greater than maxNewPeriod * discontinued plans AnnualValueForRenewal, then warn that ACV Extend is negative
		this.warningAcvExtendNegativeThereforeZero = this.acvExtend < 0;

		// Make check if totaladjustment and remaining value is greater than maxNewPeriod * discontinued plans AnnualValueForRenewal, then warn that ACV Extend is negative
		this.warningAcvIncreaseNegativeThereforeZero = this.acvIncrease < 0;

	}

	getTotalPrice(): number {
		return this.order ? this.salesService.getOrderLinesTotalPriceSum(this.order.orderLines) : 0;
	}

	getTotalAdjustmentAmount(): number {
		return this.order ? this.salesService.getTotalAdjustmentAmount(this.order) : 0;
	}

	calculateSupersededRemainingAmountForOrder(): number {
		return this.order ? this.salesService.calculateSupersededRemainingAmountForOrder(this.order) : 0;
	}

	hasExternalAdjustments(): boolean {
		return this.order ? this.salesService.hasExternalAdjustments(this.order) : false;
	}
	
}
